import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import './HealthTrendChart.css'

export type HealthTrendTone = 'teal' | 'coral' | 'blue'

export type HealthTrendPoint = {
  /** A short, human-readable period such as "2024" or "Jan 2025". */
  label: string
  value: number
}

export type HealthTrendDataset = {
  id: string
  label: string
  /** Plain-language metric name used in the y-axis label and accessible summary. */
  metricLabel: string
  unit: string
  tone?: HealthTrendTone
  /** Supply at least two chronological observations. */
  points: readonly HealthTrendPoint[]
  source: {
    label: string
    url: string
  }
  insight?: string
  caveat: string
  /** Optional display formatter. The y-axis still uses compact numeric tick labels. */
  formatValue?: (value: number) => string
}

export type HealthTrendDatasets = readonly HealthTrendDataset[]

export type HealthTrendChartProps = {
  title: string
  description?: string
  datasets: HealthTrendDatasets
  initialDatasetId?: string
  xAxisLabel?: string
  className?: string
}

type ChartPoint = HealthTrendPoint & {
  index: number
  x: number
  y: number
}

const DEFAULT_CHART_WIDTH = 720
const CHART_HEIGHT = 360

function joinClassNames(...classNames: Array<string | undefined>) {
  return classNames.filter(Boolean).join(' ')
}

function formatDefaultValue(value: number, unit: string) {
  const formatted = new Intl.NumberFormat('en', {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value)
  const joinedUnits = ['%', '°C', '°F', '‰']
  return joinedUnits.includes(unit) ? `${formatted}${unit}` : `${formatted} ${unit}`
}

function getNiceStep(value: number) {
  if (!Number.isFinite(value) || value <= 0) return 1
  const exponent = Math.floor(Math.log10(value))
  const magnitude = 10 ** exponent
  const fraction = value / magnitude

  if (fraction <= 1) return magnitude
  if (fraction <= 2) return 2 * magnitude
  if (fraction <= 2.5) return 2.5 * magnitude
  if (fraction <= 5) return 5 * magnitude
  return 10 * magnitude
}

function getYScale(values: readonly number[], preferredTickCount: number) {
  const minimum = Math.min(...values)
  const maximum = Math.max(...values)
  const naturalSpan = maximum - minimum
  const fallbackSpan = Math.max(Math.abs(maximum) * 0.2, 1)
  const paddedSpan = naturalSpan === 0 ? fallbackSpan : naturalSpan * 1.18
  const midpoint = (minimum + maximum) / 2
  const paddedMinimum = midpoint - paddedSpan / 2
  const paddedMaximum = midpoint + paddedSpan / 2
  const step = getNiceStep((paddedMaximum - paddedMinimum) / Math.max(1, preferredTickCount - 1))
  const domainMinimum = Math.floor(paddedMinimum / step) * step
  const domainMaximum = Math.ceil(paddedMaximum / step) * step
  const ticks: number[] = []

  for (let tick = domainMinimum; tick <= domainMaximum + step / 2; tick += step) {
    ticks.push(Number(tick.toPrecision(12)))
  }

  return {
    minimum: domainMinimum,
    maximum: domainMaximum === domainMinimum ? domainMinimum + step : domainMaximum,
    step,
    ticks,
  }
}

function formatAxisValue(value: number, step: number) {
  const maximumFractionDigits = step >= 1
    ? 0
    : Math.min(3, Math.max(1, Math.ceil(-Math.log10(step))))

  return new Intl.NumberFormat('en', {
    notation: Math.abs(value) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits,
  }).format(value)
}

function getTickIndices(pointCount: number, maximumTicks: number) {
  if (pointCount <= maximumTicks) {
    return Array.from({ length: pointCount }, (_, index) => index)
  }

  const indices = new Set<number>([0, pointCount - 1])
  for (let index = 1; index < maximumTicks - 1; index += 1) {
    indices.add(Math.round((index * (pointCount - 1)) / (maximumTicks - 1)))
  }
  return [...indices].sort((a, b) => a - b)
}

function shortenLabel(label: string, maximumLength = 26) {
  return label.length > maximumLength ? `${label.slice(0, maximumLength - 1)}…` : label
}

export function HealthTrendChart({
  title,
  description,
  datasets,
  initialDatasetId,
  xAxisLabel = 'Period',
  className,
}: HealthTrendChartProps) {
  const componentId = useId()
  const chartFrameRef = useRef<HTMLDivElement>(null)
  const pointRefs = useRef<Array<SVGCircleElement | null>>([])
  const [chartWidth, setChartWidth] = useState(DEFAULT_CHART_WIDTH)
  const [selectedDatasetId, setSelectedDatasetId] = useState(() => (
    datasets.some((dataset) => dataset.id === initialDatasetId)
      ? initialDatasetId as string
      : datasets[0].id
  ))
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null)
  const [focusedPointIndex, setFocusedPointIndex] = useState<number | null>(null)
  const [pinnedPointIndex, setPinnedPointIndex] = useState<number | null>(null)

  useEffect(() => {
    const frame = chartFrameRef.current
    if (!frame) return undefined

    const measure = () => {
      const nextWidth = Math.max(280, Math.round(frame.getBoundingClientRect().width))
      setChartWidth((currentWidth) => currentWidth === nextWidth ? currentWidth : nextWidth)
    }

    measure()
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    const observer = new ResizeObserver(measure)
    observer.observe(frame)
    return () => observer.disconnect()
  }, [])

  const activeDataset = datasets.find((dataset) => dataset.id === selectedDatasetId) ?? datasets[0]
  const tone = activeDataset.tone ?? 'teal'
  const formatValue = (value: number) => activeDataset.formatValue?.(value)
    ?? formatDefaultValue(value, activeDataset.unit)
  const latestPoint = activeDataset.points.at(-1) ?? activeDataset.points[0]
  const isCompact = chartWidth < 540
  const margins = {
    top: 24,
    right: isCompact ? 14 : 24,
    bottom: 66,
    left: isCompact ? 56 : 72,
  }
  const plotWidth = Math.max(1, chartWidth - margins.left - margins.right)
  const plotHeight = CHART_HEIGHT - margins.top - margins.bottom
  const plotBottom = margins.top + plotHeight
  const yScale = getYScale(activeDataset.points.map((point) => point.value), isCompact ? 4 : 5)
  const xTickIndices = getTickIndices(activeDataset.points.length, isCompact ? 3 : 6)
  const chartPoints: ChartPoint[] = activeDataset.points.map((point, index) => ({
    ...point,
    index,
    x: margins.left + (index / (activeDataset.points.length - 1)) * plotWidth,
    y: plotBottom - ((point.value - yScale.minimum) / (yScale.maximum - yScale.minimum)) * plotHeight,
  }))
  const linePath = chartPoints.map((point, index) => (
    `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`
  )).join(' ')
  const interactionIndex = focusedPointIndex ?? hoveredPointIndex ?? pinnedPointIndex
  const interactionPoint = interactionIndex === null ? null : chartPoints[interactionIndex]

  const selectDataset = (datasetId: string) => {
    setSelectedDatasetId(datasetId)
    setHoveredPointIndex(null)
    setFocusedPointIndex(null)
    setPinnedPointIndex(null)
  }

  const handlePointKeyDown = (
    event: ReactKeyboardEvent<SVGCircleElement>,
    pointIndex: number,
  ) => {
    let nextIndex: number | null = null

    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = Math.max(0, pointIndex - 1)
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = Math.min(chartPoints.length - 1, pointIndex + 1)
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = chartPoints.length - 1
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setPinnedPointIndex((current) => current === pointIndex ? null : pointIndex)
      return
    }

    if (nextIndex !== null) {
      event.preventDefault()
      pointRefs.current[nextIndex]?.focus()
    }
  }

  const tooltip = interactionPoint
    ? (() => {
        const tooltipWidth = Math.min(210, Math.max(142, plotWidth - 10))
        const tooltipHeight = 58
        const fitsOnRight = interactionPoint.x + tooltipWidth + 14 <= chartWidth - margins.right
        const x = fitsOnRight
          ? interactionPoint.x + 12
          : Math.max(margins.left + 2, interactionPoint.x - tooltipWidth - 12)
        const fitsAbove = interactionPoint.y - tooltipHeight - 12 >= margins.top
        const y = fitsAbove ? interactionPoint.y - tooltipHeight - 10 : interactionPoint.y + 12
        return { x, y, width: tooltipWidth, height: tooltipHeight }
      })()
    : null

  return (
    <section
      className={joinClassNames('health-trend-chart', `health-trend-chart--${tone}`, className)}
      aria-labelledby={`${componentId}-title`}
    >
      <div className="health-trend-chart__header">
        <div className="health-trend-chart__heading">
          <h2 id={`${componentId}-title`}>{title}</h2>
          {description && <p>{description}</p>}
        </div>

        <div className="health-trend-chart__latest" role="status" aria-live="polite" aria-atomic="true">
          <span>Latest observation</span>
          <strong>{formatValue(latestPoint.value)}</strong>
          <small>{latestPoint.label} · {activeDataset.label}</small>
        </div>
      </div>

      <div className="health-trend-chart__controls">
        <span className="health-trend-chart__controls-label">Choose a dataset</span>
        <div className="health-trend-chart__selectors" role="group" aria-label="Choose a health dataset">
          {datasets.map((dataset) => (
            <button
              key={dataset.id}
              className={joinClassNames(
                'health-trend-chart__selector',
                `health-trend-chart__selector--${dataset.tone ?? 'teal'}`,
              )}
              type="button"
              aria-pressed={activeDataset.id === dataset.id}
              onClick={() => selectDataset(dataset.id)}
            >
              <span aria-hidden="true" />
              {dataset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="health-trend-chart__plot" ref={chartFrameRef}>
        <svg
          viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
          width={chartWidth}
          height={CHART_HEIGHT}
          role="group"
          aria-labelledby={`${componentId}-chart-title ${componentId}-chart-description`}
        >
          <title id={`${componentId}-chart-title`}>{`${activeDataset.label} over time`}</title>
          <desc id={`${componentId}-chart-description`}>{`${activeDataset.metricLabel} shown across ${activeDataset.points.length} periods, from ${activeDataset.points[0].label} to ${latestPoint.label}. Use Tab to inspect points and arrow keys to move between them.`}</desc>

          <rect
            className="health-trend-chart__plot-frame"
            x={margins.left}
            y={margins.top}
            width={plotWidth}
            height={plotHeight}
          />

          {yScale.ticks.map((tick) => {
            const y = plotBottom - ((tick - yScale.minimum) / (yScale.maximum - yScale.minimum)) * plotHeight
            return (
              <g key={tick} aria-hidden="true">
                <line
                  className="health-trend-chart__grid-line"
                  x1={margins.left}
                  x2={margins.left + plotWidth}
                  y1={y}
                  y2={y}
                />
                <text
                  className="health-trend-chart__axis-tick"
                  x={margins.left - 10}
                  y={y + 4}
                  textAnchor="end"
                >
                  {formatAxisValue(tick, yScale.step)}
                </text>
              </g>
            )
          })}

          {xTickIndices.map((pointIndex) => {
            const point = chartPoints[pointIndex]
            const isFirst = pointIndex === 0
            const isLast = pointIndex === chartPoints.length - 1
            return (
              <g key={`${point.label}-${pointIndex}`} aria-hidden="true">
                <line
                  className="health-trend-chart__axis-mark"
                  x1={point.x}
                  x2={point.x}
                  y1={plotBottom}
                  y2={plotBottom + 6}
                />
                <text
                  className="health-trend-chart__axis-tick"
                  x={point.x}
                  y={plotBottom + 24}
                  textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
                >
                  {shortenLabel(point.label, isCompact ? 12 : 18)}
                </text>
              </g>
            )
          })}

          <text
            className="health-trend-chart__axis-title"
            x={margins.left + plotWidth / 2}
            y={CHART_HEIGHT - 8}
            textAnchor="middle"
            aria-hidden="true"
          >
            {xAxisLabel}
          </text>
          <text
            className="health-trend-chart__axis-title"
            x={16}
            y={margins.top + plotHeight / 2}
            textAnchor="middle"
            transform={`rotate(-90 16 ${margins.top + plotHeight / 2})`}
            aria-hidden="true"
          >
            {shortenLabel(`${activeDataset.metricLabel} (${activeDataset.unit})`, isCompact ? 34 : 48)}
          </text>

          <path className="health-trend-chart__line" d={linePath} aria-hidden="true" />

          {chartPoints.map((point) => {
            const isActive = interactionPoint?.index === point.index
            return (
              <g key={`${point.label}-${point.index}`}>
                <circle
                  ref={(node) => { pointRefs.current[point.index] = node }}
                  className="health-trend-chart__point-hit"
                  cx={point.x}
                  cy={point.y}
                  r={18}
                  role="button"
                  tabIndex={0}
                  aria-label={`${point.label}: ${formatValue(point.value)}. Use arrow keys for adjacent points.`}
                  aria-pressed={pinnedPointIndex === point.index}
                  onPointerEnter={() => setHoveredPointIndex(point.index)}
                  onPointerLeave={() => setHoveredPointIndex(null)}
                  onFocus={() => setFocusedPointIndex(point.index)}
                  onBlur={() => setFocusedPointIndex(null)}
                  onPointerDown={(event) => event.currentTarget.focus()}
                  onClick={() => setPinnedPointIndex((current) => current === point.index ? null : point.index)}
                  onKeyDown={(event) => handlePointKeyDown(event, point.index)}
                />
                <circle
                  className={joinClassNames('health-trend-chart__point', isActive ? 'is-active' : undefined)}
                  cx={point.x}
                  cy={point.y}
                  r={isActive ? 6 : 4.5}
                  aria-hidden="true"
                />
              </g>
            )
          })}

          {interactionPoint && tooltip && (
            <g className="health-trend-chart__tooltip" aria-hidden="true" pointerEvents="none">
              <rect x={tooltip.x} y={tooltip.y} width={tooltip.width} height={tooltip.height} rx={10} />
              <text x={tooltip.x + 12} y={tooltip.y + 22}>
                {shortenLabel(interactionPoint.label)}
              </text>
              <text className="health-trend-chart__tooltip-value" x={tooltip.x + 12} y={tooltip.y + 43}>
                {formatValue(interactionPoint.value)}
              </text>
            </g>
          )}
        </svg>
      </div>

      {activeDataset.insight && <p className="health-trend-chart__insight"><strong>What the selected series shows:</strong> {activeDataset.insight}</p>}

      <div className="health-trend-chart__notes">
        <p>
          <strong>Source:</strong>{' '}
          <a href={activeDataset.source.url} target="_blank" rel="noopener noreferrer">
            {activeDataset.source.label} <span aria-hidden="true">↗</span>
            <span className="health-trend-chart__sr-only"> (opens in a new tab)</span>
          </a>
        </p>
        <p><strong>Chart scale:</strong> The vertical axis is focused from {formatAxisValue(yScale.minimum, yScale.step)} to {formatAxisValue(yScale.maximum, yScale.step)} {activeDataset.unit}; it does not start at zero.</p>
        <p><strong>Interpret with care:</strong> {activeDataset.caveat}</p>
      </div>

      <details className="health-trend-chart__data-table">
        <summary>View {activeDataset.label} as a table</summary>
        <div>
          <table>
            <caption>{activeDataset.label} values</caption>
            <thead>
              <tr>
                <th scope="col">{xAxisLabel}</th>
                <th scope="col">{activeDataset.metricLabel}</th>
              </tr>
            </thead>
            <tbody>
              {activeDataset.points.map((point, index) => (
                <tr key={`${point.label}-${index}`}>
                  <th scope="row">{point.label}</th>
                  <td>{formatValue(point.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  )
}
